import { ChangeDetectionStrategy, inject, Component, Inject, OnChanges, OnInit, SimpleChanges, input, signal } from '@angular/core';
import { LoggerService } from '@/core/services/logger.service';

import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { CardContent } from '@/shared/components/card-content/card-content';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { ConsultorioAnotacoesFeatureService } from './consultorio-anotacoes.service';
import { ConsultorioAtualizacaoRequest } from '@/core/api/sicom-api/registro/medico/consultorio-privado/consultorio-atualizacao.model';
import { finalize } from 'rxjs/operators';
import { ActionChildComponent } from '../../../../../../../shared/components/containers/action-child.component';
import { BotaoAcoesType } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { MenuItem } from 'primeng/api';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { NovaAnotacaoDialog } from '@/shared/components/anotacoes/dialogs/nova-anotacao/nova-anotacao';
import { AuthService } from '@/core/auth/auth.service';
import { PessoaService } from '@/shared/components/pessoa/pessoa-detalhe/pessoa.service';
import { firstValueFrom } from 'rxjs';
import { CommonModule } from '@angular/common';
import { comIdempotencia, novaChaveIdempotencia } from 'src/interceptors/idempotencia-interceptor';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

@Component({
    selector: 'app-consultorio-anotacoes',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, CardContent, ButtonModule, TooltipModule, CustomTableComponent, NovaAnotacaoDialog],
    templateUrl: './consultorio-anotacoes.html',
    styleUrl: './consultorio-anotacoes.scss'
})
/** Aba de anotações do consultório privado. */
export class ConsultorioAnotacoesComponent extends ActionChildComponent implements OnChanges, OnInit {
    private readonly logger = inject(LoggerService);
    constructor(
        @Inject(ConsultorioAnotacoesFeatureService)
        private feature: ConsultorioAnotacoesFeatureService,
        private messageService: MessageService,
        private authService: AuthService,
        private pessoaService: PessoaService
    ) {
        super();
    }

    protected readonly roleConsultorioEditar = PERMISSOES.medicos.consultorio.editar;

    // Pessoa de contexto (para consulta)
    pessoa = input<Pessoa>();
    // Consultório selecionado
    idConsultorio = input<number | null>(null);

    data: string | null = null; // campos não utilizados em leitura
    historico: string | null = null; // idem

    // Linhas exibidas na tabela de anotações
    anotacoes = signal<{ data: string; historico: string }[]>([]);

    // Estados
    loading = signal(false);
    erro = signal<string | undefined>(undefined);
    modalNovaAnotacao = signal(false);

    /**
     * Chave de idempotência da sessão do diálogo: nasce ao abrir. Aqui pesa mais que nas outras
     * telas — PRIV_CONSULTORIO_ATUALIZACAO não tem chave primária, então sem isto nada no banco
     * impede a linha repetida.
     */
    private chaveIdempotencia = novaChaveIdempotencia();
    idUsuarioLogado?: number;
    motivoIdUsuarioNaoResolvido?: string;

    tableMetadata: TableMetadata = {
        headers: {
            data: { type: 'text', value: 'Data', hidden: false, default: '' },
            historico: { type: 'text', value: 'Histórico Médico', hidden: false, default: '' }
        },
        title: 'historico',
        actions: {
            remover: {
                label: '',
                icon: 'pi pi-trash',
                color: 'danger',
                permission: this.roleConsultorioEditar,
                command: (item: unknown) => this.deletar(item)
            }
        }
    };

    actions: BotaoAcoesType & { useEdit: boolean } = {
        items: [
            {
                label: 'Adicionar',
                icon: 'pi pi-plus-circle',
                command: () => this.novo(),
                styleClass: 'bg-green-500',
                permission: this.roleConsultorioEditar
            }
        ],
        useEdit: false
    };

    override initComponent(): void {}

    override ngOnInit(): void {
        super.ngOnInit();
        this.carregarIdUsuarioLogado();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['pessoa'] || changes['idConsultorio']) {
            const p = this.pessoa();
            const idC = this.idConsultorio();
            if (p && p.id !== undefined && p.id !== null && !!p.uf && typeof idC === 'number') {
                this.carregar({ id: p.id, uf: p.uf, idConsultorio: idC });
            } else {
                this.anotacoes.set([]);
            }
        }
    }

    private carregar(params: Pessoa & { idConsultorio: number }): void {
        this.loading.set(true);
        this.erro.set(undefined);
        this.feature
            .buscar(params)
            .pipe(finalize(() => this.loading.set(false)))
            .subscribe({
                next: (lista: ConsultorioAtualizacaoRequest[] | null | undefined) => {
                    this.anotacoes.set(
                        (lista ?? []).map((r: ConsultorioAtualizacaoRequest) => ({
                            ...r,
                            data: this.formatarData(r.dtAtualizacao),
                            historico: r.txAtualizacao ?? ''
                        }))
                    );
                },
                error: (err: unknown) => {
                    this.logger.error('Erro ao buscar atualizações do consultório', err);
                    this.anotacoes.set([]);
                    this.erro.set('Falha ao carregar anotações do consultório.');
                }
            });
    }

    private formatarData(valor?: string | null): string {
        if (!valor) return '';
        const m = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) {
            const [, yyyy, mm, dd] = m;
            return `${dd}/${mm}/${yyyy}`;
        }
        try {
            const d = new Date(valor);
            if (isNaN(d.getTime())) return '';
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}/${mm}/${yyyy}`;
        } catch {
            return '';
        }
    }

    override getDefinedActions(): BotaoAcoesType & { useEdit: boolean } {
        return this.actions;
    }

    novo(): void {
        this.chaveIdempotencia = novaChaveIdempotencia();
        this.modalNovaAnotacao.set(true);
    }

    async onAnotacaoCriada(payload: { dtHistoricoMedico: string; txHistoricoMedico: string }): Promise<void> {
        const p = this.pessoa();
        const idConsultorio = this.idConsultorio();
        if (!p || p.id == null || !p.uf || typeof idConsultorio !== 'number') {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Dados do consultório não informados.' });
            return;
        }
        if (typeof this.idUsuarioLogado !== 'number') {
            await this.carregarIdUsuarioLogado();
        }
        if (typeof this.idUsuarioLogado !== 'number') {
            this.logger.warn('Falha ao resolver idUsuarioLogado para anotação de consultório', { motivo: this.motivoIdUsuarioNaoResolvido });
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Usuário logado não identificado para salvar anotação.' });
            return;
        }

        this.loading.set(true);
        this.feature
            .inserirAnotacao(
                { id: p.id, uf: p.uf },
                {
                    idConsultorio,
                    idUsuario: this.idUsuarioLogado,
                    dtAtualizacao: payload.dtHistoricoMedico ? `${payload.dtHistoricoMedico}T00:00:00` : undefined,
                    txAtualizacao: payload.txHistoricoMedico
                },
                comIdempotencia(this.chaveIdempotencia)
            )
            .pipe(finalize(() => this.loading.set(false)))
            .subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Anotação inserida com sucesso.' });
                    this.modalNovaAnotacao.set(false);
                    this.carregar({ id: p.id, uf: p.uf, idConsultorio });
                },
                error: (err) => {
                    // Sem toast local: o interceptor global já exibiu a mensagem do backend. O diálogo
                    // só fecha no ramo de sucesso, então o texto digitado continua disponível.
                    this.logger.error('Erro ao inserir anotação do consultório', err);
                }
            });
    }

    private async carregarIdUsuarioLogado(): Promise<void> {
        try {
            const userInfo = await this.authService.getUserInfo();
            const cpf = (userInfo?.cpf ?? '').replace(/\D/g, '');
            const uf = this.authService.ufDoUsuario();

            if (!cpf || !uf) {
                this.idUsuarioLogado = undefined;
                this.motivoIdUsuarioNaoResolvido = !cpf ? 'CPF ausente no token' : 'UF ausente no token';
                return;
            }

            const cpfFormatado = this.formatarCpf(cpf);
            const ufUpper = uf.toUpperCase();
            const ufLower = uf.toLowerCase();

            let pessoaLogada = await firstValueFrom(this.pessoaService.localizarPorRegistroUf(cpf, ufUpper));
            if (!pessoaLogada && cpfFormatado) {
                pessoaLogada = await firstValueFrom(this.pessoaService.localizarPorRegistroUf(cpfFormatado, ufUpper));
            }
            if (!pessoaLogada) {
                pessoaLogada = await firstValueFrom(this.pessoaService.localizarPorRegistroUf(cpf, ufLower));
            }
            if (!pessoaLogada && cpfFormatado) {
                pessoaLogada = await firstValueFrom(this.pessoaService.localizarPorRegistroUf(cpfFormatado, ufLower));
            }

            const idPessoa = Number(pessoaLogada?.id);
            const idValido = Number.isInteger(idPessoa) && idPessoa > 0 ? idPessoa : undefined;
            this.idUsuarioLogado = idValido;
            this.motivoIdUsuarioNaoResolvido = idValido ? undefined : 'Pessoa não localizada por CPF/UF ou resposta sem id';
        } catch (erro) {
            this.idUsuarioLogado = undefined;
            this.motivoIdUsuarioNaoResolvido = 'Erro ao consultar pessoa por CPF/UF';
            this.logger.error('Erro ao resolver idUsuarioLogado por CPF/UF', erro);
        }
    }

    private formatarCpf(cpf: string): string {
        const valor = (cpf ?? '').replace(/\D/g, '');
        if (valor.length !== 11) return '';
        return `${valor.slice(0, 3)}.${valor.slice(3, 6)}.${valor.slice(6, 9)}-${valor.slice(9)}`;
    }
    deletar(item: unknown) {
        this.logger.info('DELETAR', item);
    }

    override updateActionsWithIndexes(actions: [MenuItem, number][]) {}
    override toggleAction(action: MenuItem): MenuItem {
        return {};
    }
}
